import React, { useMemo, useRef, useState } from 'react';

// Simple simulated terminal that validates commands in order based on steps
// expectedFlag: if provided, auto-submits flag when all steps complete
export const SimTerminal = ({ steps = [], onComplete, expectedFlag = '' }) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]); // { text, type: 'in' | 'ok' | 'err' }
  const [idx, setIdx] = useState(0);
  const endRef = useRef(null);

  const required = useMemo(() => {
    const isCommandLike = (s) => typeof s === 'string' && /[a-zA-Z/]/.test(s);
    return steps
      .map((s) => (typeof s === 'object' ? s.comando : s))
      .filter(isCommandLike)
      .map((s) => String(s));
  }, [steps]);

  const normalize = (s) => String(s).trim().replace(/\s+/g, ' ').toLowerCase();

  const expected = required[idx] || '';
  const expectedHint = expected ? `Siguiente: ${expected}` : 'Todos los comandos completados';

  const pushHistory = (entry) => {
    setHistory((h) => [...h, entry]);
    // Scroll to end on next tick
    setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  };

  const handleEnter = (e) => {
    e.preventDefault();
    const value = input;
    if (!value.trim()) return;

    pushHistory({ text: `$ ${value}`, type: 'in' });

    if (expected) {
      const ok = normalize(value).includes(normalize(expected));
      if (ok) {
        pushHistory({ text: 'Correcto ✅', type: 'ok' });
        const next = idx + 1;
        setIdx(next);
        if (next >= required.length) {
          pushHistory({ text: 'Todos los comandos requeridos completados. 🎉', type: 'ok' });
          // Auto-submit flag if provided and all steps done
          if (expectedFlag && onComplete) {
            setTimeout(() => {
              onComplete(expectedFlag);
            }, 500);
          } else if (onComplete) {
            onComplete();
          }
        }
      } else {
        pushHistory({ text: 'Comando no coincide con el paso actual.', type: 'err' });
      }
    } else {
      pushHistory({ text: 'No hay más comandos requeridos en este nivel.', type: 'ok' });
    }

    setInput('');
  };

  const progress = required.length === 0 ? 100 : Math.round((idx / required.length) * 100);

  return (
    <div className="sim-terminal">
      <div className="sim-terminal-bar">
        <div className="sim-terminal-title">bash</div>
        <div className="sim-terminal-progress">
          <div className="sim-terminal-progress-inner" style={{ width: `${progress}%` }} />
        </div>
        <div className="sim-terminal-hint">{expectedHint}</div>
      </div>
      <div className="sim-terminal-screen">
        {history.map((h, i) => (
          <div key={i} className={`sim-line ${h.type}`}>{h.text}</div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={handleEnter} className="sim-terminal-input">
        <span className="prompt">$</span>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={expected ? expected : 'Escribe un comando'}
          aria-label="Simulated terminal input"
        />
        <button type="submit" className="btn-enter">Enter</button>
      </form>
    </div>
  );
};
